// The hover bar's More menu (desktop), Discord-style: recent emoji along the top, then the message's actions in
// groups — react, respond, the text itself, organize, delete. Opens under the More button (above it near the bottom of
// the window), right-aligned with it so the bar stays visible;
// arrow keys move through it, Escape closes it and gives focus back to the button. Phones use the bottom sheet in
// MessageList instead.
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReactionGlyph, reactionLabel, useServerEmoji, useUsedHere } from "../customEmoji.tsx";
import type { QuickHeld, QuickHold } from "./useQuickHold.tsx";

/**
 * The message actions, in order and grouped, on every surface: More, right-click and the phone's sheet (#143). The
 * same order as iOS's long-press sheet (MessageActionsSheet), so none of them drift. A surface leaves out what it
 * shows elsewhere (the phone sheet's pins are badges above the list).
 */
export const ACTION_GROUPS = [["reply", "list", "thread", "forward"], ["edit", "copy", "link", "download"], ["pin", "unread"], ["delete"]] as const;
export type ActionKey = (typeof ACTION_GROUPS)[number][number];

export const MENU_ICONS: Record<string, React.ReactNode> = {
  reply: <><path d="M9 14 4 9l5-5" /><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" /></>,
  forward: <><path d="m15 14 5-5-5-5" /><path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5V20" /></>,
  thread: <><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /><path d="M8 10h8M8 14h5" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
  copy: <><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></>,
  delete: <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />,
  list: <><path d="M17 13.5V21l-5-3.5L7 21V5a2 2 0 0 1 2-2h4" /><path d="M18 3v6M15 6h6" /></>,
  pin: <><path d="M12 17v5" /><path d="M9 10.8V4h6v6.8l3 3.2v2H6v-2z" /></>,
  unread: <><path d="M21 12.5V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h9.5" /><circle cx="19" cy="6" r="3" /></>,
  link: <><path d="M10 14a5 5 0 0 0 7.1 0l3-3a5 5 0 0 0-7.1-7.1l-1 1" /><path d="M14 10a5 5 0 0 0-7.1 0l-3 3a5 5 0 0 0 7.1 7.1l1-1" /></>,
  download: <><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></>,
  react: <><circle cx="12" cy="12" r="9" /><path d="M8.5 14.5c1 1.2 2.1 1.8 3.5 1.8s2.5-.6 3.5-1.8M9 9.5h.01M15 9.5h.01" /></>,
};

export function MoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.9" />
      <circle cx="12" cy="12" r="1.9" />
      <circle cx="19" cy="12" r="1.9" />
    </svg>
  );
}

const W = 232;
const EDGE = 8;

export interface MoreMenuProps {
  anchor: DOMRect;
  /** Opened from the keyboard: focus starts on the first item (from the mouse, on the menu itself). */
  keyboard: boolean;
  /** The More button: pressing it again closes the menu through its own click, not as an outside click. Absent for
   * a right-click, which opens the menu at `at`, the pointer. */
  button?: HTMLElement;
  at?: { x: number; y: number };
  /** Emoji I've already reacted with on this message (shown pressed). */
  mine: (emoji: string) => boolean;
  onReact: (emoji: string) => void;
  /** The recent emoji hold like the picker's (#726): the hold, and what a super or jumbo hold on one does. */
  quick: QuickHold;
  onReactHeld: (emoji: string, how: QuickHeld) => void;
  /** Opens the full picker; `at` is where the menu's "Add reaction" row was. */
  onAddReaction: (at: DOMRect) => void;
  onReply: () => void;
  /** Opens the forward picker. Absent where a message can't be forwarded (quiet lines). */
  onForward?: () => void;
  /** Absent where threads aren't offered (inside a thread, DMs). */
  onThread?: () => void;
  hasThread: boolean;
  onEdit?: () => void;
  onCopy?: () => void;
  /** Copy a link to this message. Absent where there are no message links (blooming threads, unsent messages). */
  onCopyLink?: () => void;
  /** Pictures, videos and GIFs on the message: save them. */
  download?: { label: string; run: () => void };
  /** Absent where there are no lists (system lines). */
  pin?: { pinned: boolean; toggle: () => void; addToList: (at: DOMRect) => void };
  /** Mark unread from this message. Absent inside threads and for messages not sent yet. */
  onMarkUnread?: () => void;
  /** `confirm` false: Shift-click, delete without asking. */
  onDelete?: (confirm: boolean) => void;
  onClose: (refocus: boolean) => void;
}

export function MoreMenu(p: MoreMenuProps) {
  const box = useRef<HTMLDivElement>(null);
  const recent = useUsedHere("recent", 4);
  const { byId } = useServerEmoji();
  const [pos, setPos] = useState<React.CSSProperties>({ position: "fixed", top: p.at?.y ?? p.anchor.bottom + 4, left: p.at?.x ?? p.anchor.right - W, opacity: 0 });

  // Under the button, right edges lined up; above it when there's no room below; always on screen. A right-click's
  // menu opens at the pointer instead, its top-left corner there, flipped left or up near the window's edges.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (p.at) {
      const left = p.at.x + W <= vw - EDGE ? p.at.x : Math.max(EDGE, p.at.x - W);
      const top = p.at.y + h <= vh - EDGE ? p.at.y : Math.max(EDGE, p.at.y - h);
      setPos({ position: "fixed", top, left });
      return;
    }
    const a = p.anchor;
    const left = Math.max(EDGE, Math.min(a.right - W, vw - EDGE - W));
    const below = a.bottom + 4;
    const above = a.top - 4 - h;
    const top = below + h <= vh - EDGE ? below : above >= EDGE ? above : Math.max(EDGE, vh - EDGE - h);
    setPos({ position: "fixed", top, left });
    // Follows its button if the row moves (new messages while pinned to the bottom).
  }, [p.anchor.top, p.anchor.left, p.at?.x, p.at?.y]);

  useLayoutEffect(() => {
    (p.keyboard ? box.current?.querySelector<HTMLElement>("[role=menuitem]") : box.current)?.focus({ preventScroll: true });
    const onDown = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && !p.button?.contains(e.target as Node) && p.onClose(false);
    const onResize = () => p.onClose(false);
    // Escape with focus elsewhere (the mouse moved on and something else took it) still closes it.
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || box.current?.contains(document.activeElement)) return;
      e.preventDefault();
      p.onClose(true);
    };
    document.addEventListener("keydown", onEsc);
    // Next tick, so the click that opened it doesn't close it.
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    window.addEventListener("resize", onResize);
    window.addEventListener("blur", onResize);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("blur", onResize);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const onKey = (e: React.KeyboardEvent) => {
    const items = [...(box.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => items[(i + items.length) % items.length]?.focus();
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      p.onClose(true);
    } else if (e.key === "Tab") {
      e.preventDefault();
      p.onClose(true);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      go(at + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      go(at < 0 ? items.length - 1 : at - 1);
    } else if ((e.key === "ArrowRight" || e.key === "ArrowLeft") && at >= 0 && items[at]!.classList.contains("mm-emoji")) {
      // Across the emoji row.
      e.preventDefault();
      const row = items.filter((x) => x.classList.contains("mm-emoji"));
      const i = row.indexOf(items[at]!);
      row[(i + (e.key === "ArrowRight" ? 1 : row.length - 1)) % row.length]?.focus();
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      go(e.key === "Home" ? 0 : items.length - 1);
    }
  };

  const rect = (e: React.MouseEvent) => (e.currentTarget as HTMLElement).getBoundingClientRect();
  const done = (f: () => void) => () => {
    f();
    p.onClose(false);
  };
  const items: Record<ActionKey, React.ReactNode> = {
    reply: <Item key="reply" icon="reply" label="Reply" onClick={done(p.onReply)} />,
    list: p.pin && <Item key="list" icon="list" label="Add to collection…" hint="L" onClick={(e) => { const r = rect(e); p.onClose(false); p.pin!.addToList(r); }} />,
    thread: p.onThread && <Item key="thread" icon="thread" label={p.hasThread ? "Open thread" : "Reply in thread"} onClick={done(p.onThread)} />,
    forward: p.onForward && <Item key="forward" icon="forward" label="Forward" onClick={done(p.onForward)} />,
    edit: p.onEdit && <Item key="edit" icon="edit" label="Edit message" onClick={done(p.onEdit)} />,
    copy: p.onCopy && <Item key="copy" icon="copy" label="Copy text" onClick={done(p.onCopy)} />,
    link: p.onCopyLink && <Item key="link" icon="link" label="Copy message link" onClick={done(p.onCopyLink)} />,
    download: p.download && <Item key="download" icon="download" label={p.download.label} onClick={done(p.download.run)} />,
    pin: p.pin && <Item key="pin" icon="pin" label={p.pin.pinned ? "Unpin message" : "Pin message"} hint="P" onClick={done(p.pin.toggle)} />,
    unread: p.onMarkUnread && <Item key="unread" icon="unread" label="Mark unread" hint="Alt+click" onClick={done(p.onMarkUnread)} />,
    delete: p.onDelete && <Item key="delete" icon="delete" label="Delete message" danger title="Shift-click to skip confirming" onClick={(e) => { const skip = e.shiftKey; p.onClose(false); p.onDelete!(!skip); }} />,
  };
  const groups: React.ReactNode[][] = [
    [<Item key="react" icon="react" label="Add reaction" chevron onClick={(e) => { const r = rect(e); p.onClose(false); p.onAddReaction(r); }} />],
    ...ACTION_GROUPS.map((g) => g.map((k) => items[k]).filter(Boolean)),
  ];

  return createPortal(
    <div className="msg-more" ref={box} style={{ ...pos, width: W }} role="menu" aria-label="More message actions" tabIndex={-1} onKeyDown={onKey}>
      <div className="mm-quick" role="group" aria-label="Recent reactions">
        {recent.map((q) => {
          const on = p.mine(q);
          return (
            <button
              key={q}
              type="button"
              role="menuitem"
              className={`mm-emoji${on ? " on" : ""}`}
              aria-pressed={on}
              aria-label={`React ${reactionLabel(q, byId)}`}
              title={`React ${reactionLabel(q, byId)}`}
              {...p.quick.bind(q, (how) => {
                p.onClose(false);
                p.onReactHeld(q, how);
              })}
              onClick={() => {
                if (!p.quick.swallowClick()) done(() => p.onReact(q))();
              }}
            >
              <ReactionGlyph emoji={q} />
            </button>
          );
        })}
      </div>
      {groups
        .filter((g) => g.length)
        .map((g, i) => (
          <div key={i} className="mm-group" role="group">
            {g}
          </div>
        ))}
    </div>,
    document.body,
  );
}

function Item({ icon, label, hint, chevron, danger, title, onClick }: { icon: string; label: string; hint?: string; chevron?: boolean; danger?: boolean; title?: string; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button type="button" role="menuitem" className={`mm-item${danger ? " danger" : ""}`} title={title} onClick={onClick}>
      <svg className="mm-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {MENU_ICONS[icon]}
      </svg>
      <span className="mm-label">{label}</span>
      {hint && <kbd className="mm-hint" aria-hidden="true">{hint}</kbd>}
      {chevron && (
        <svg className="mm-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m9 6 6 6-6 6" />
        </svg>
      )}
    </button>
  );
}
